<template>
  <div class="checkbox__wrap" :class="checkboxStyleModificators">
    <label
      v-if="labelAsHtml"
      v-focusable="{ attachTabIndex: false }"
      :tabindex="labelTabIndex"
      class="checkbox__label checkbox__html"
      key="html-label"
    >
      <input
        tabindex="-1"
        type="checkbox"
        class="checkbox__input"
        :checked="value"
        :disabled="disabled"
        @click="toggle"
      />
      <span v-html="sanitizedLabel" class="checkbox__label-text" />
    </label>

    <label
      v-else
      v-focusable="{ attachTabIndex: false }"
      :tabindex="labelTabIndex"
      class="checkbox__label"
      key="text-label"
    >
      <input
        tabindex="-1"
        type="checkbox"
        class="checkbox__input"
        :checked="value"
        :disabled="disabled"
        @click="toggle"
      />
      <slot v-if="hasSlot" />
      <a-link
        v-else-if="labelHref"
        :href="labelHref"
        :rel="rel"
        :target="target"
      >
        <span class="checkbox__label-text">{{ label }}</span>
      </a-link>
      <span v-else class="checkbox__label-text">
        {{ label }}
      </span>
    </label>
    <a-validation-message :error="error" />
  </div>
</template>

<script>
import { prop } from 'ramda'
import AValidationMessage from 'shared/AValidationMessage'
import { parseAndSanitizeHtml } from '@/utils/helpers/processHtml/parseAndSanitizeHtml'
import { HTML_TAG } from '@fmpedia/enums'

export const CHECKBOX_STYLE = {
  BORDERED: 'BORDERED',
  REGULAR: 'REGULAR'
}

const CHECKBOX_STYLE_VALUES = Object.values(CHECKBOX_STYLE)

export default {
  name: 'ACheckbox',
  components: { AValidationMessage },
  props: {
    label: {
      type: String
    },
    labelHref: {
      type: String,
      default: ''
    },
    rel: {
      type: String
    },
    error: {
      type: Object,
      default: () => ({})
    },
    value: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    target: {
      type: Boolean
    },
    labelAsHtml: {
      type: Boolean,
      default: false
    },
    checkboxStyle: {
      validator: value => CHECKBOX_STYLE_VALUES.includes(value),
      default: CHECKBOX_STYLE.BORDERED
    }
  },
  consts: {
    HTML_TAG
  },
  computed: {
    labelTabIndex() {
      return this.disabled ? -1 : 0
    },
    checkboxStyleModificators() {
      return {
        'checkbox--regular': this.checkboxStyle === CHECKBOX_STYLE.REGULAR,
        'checkbox--disabled': this.disabled,
        'checkbox--checked': this.value
      }
    },
    sanitizedLabel() {
      return parseAndSanitizeHtml(this.label)
    },
    hasSlot() {
      return prop('length', this.$slots.default)
    }
  },
  methods: {
    toggle() {
      this.$emit('input', !this.value)
    }
  }
}
</script>

<style lang="scss" scoped>
.checkbox__wrap {
  width: 100%;
  min-height: 20px;
  user-select: none;
  color: $c--white;
  position: relative;
  display: inline-block;
  cursor: pointer;

  .checkbox__input {
    position: absolute;
    opacity: 0;
    cursor: inherit;
  }

  .checkbox__label {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    font-size: 18px;
    line-height: 28px;
    max-height: inherit;
    min-height: inherit;
    overflow: hidden;
    padding-left: 30px;
    cursor: inherit;

    &:focus {
      outline: none;

      &::before {
        border: 2px solid $c--white;
      }
    }
  }

  .checkbox__label * {
    margin-top: 1px;
    display: block;
  }

  .checkbox__label::before,
  .checkbox__label::after {
    content: '';
    position: absolute;
    display: inline-block;
  }

  .checkbox__label::before {
    height: 20px;
    width: 20px;
    background-color: $c--black;
    border: 1px solid $c--gray;
    border-radius: 4px;
    left: 0;
    top: 4px;
  }

  &.checkbox--disabled {
    cursor: not-allowed;

    .checkbox__label {
      color: $c--disabled-text;

      &::before {
        border-color: $c--disabled-border;
      }
    }
  }

  &.checkbox--checked {
    .checkbox__label {
      &::after {
        content: '';
        height: 6px;
        width: 11px;
        border-left: 2px solid;
        border-bottom: 2px solid;
        color: $c--black;
        transform: rotate(-45deg);
        left: 5px;
        top: 9px;
      }

      &::before {
        background-color: $c--white;
        border-color: transparent;
      }
    }

    &.checkbox--disabled .checkbox__label::before {
      background-color: $c--disabled-border;
    }
  }

  .checkbox__label-text {
    /deep/ a {
      text-decoration: underline;
    }
  }

  &.checkbox--regular .checkbox__label-text {
    /deep/ * {
      color: $c--white;
    }
  }
}
</style>
